import React from 'react';
import { OrganizationSubscription } from './types';

interface OrganizationsGridProps {
  data: OrganizationSubscription[];
  onCardClick: (org: OrganizationSubscription) => void;
}

const OrganizationsGrid: React.FC<OrganizationsGridProps> = ({
  data,
  onCardClick,
}) => {
  return (
    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'>
      { data.map((org) => {
        const status = org.subscription.candidateFeature?.status ?? 'unknown';
        const statusColors: Record<string, string> = {
          active: 'bg-emerald-50 text-emerald-600 border-emerald-100',
          trialing: 'bg-amber-50 text-amber-600 border-amber-100',
          canceled: 'bg-red-50 text-red-600 border-red-100',
          past_due: 'bg-orange-50 text-orange-600 border-orange-100',
        };

        return (
          <div
              key={org.organization.id}
              onClick={() => onCardClick(org)}
              className='bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 group cursor-pointer relative'
          >
            <div className='flex justify-between items-start mb-6'>
              <div className='w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-600 group-hover:bg-emerald-600 group-hover:text-white transition-all duration-300'>
                { org.organization.companyLogo ? (
                  <img
                      src={org.organization.companyLogo}
                      alt={org.organization.organizationName}
                      className='w-8 h-8 object-contain rounded-lg'
                  />
                ) : (
                  <svg className='w-6 h-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={1.5}
                        d='M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4'
                    />
                  </svg>
                ) }
              </div>
              <span
                  className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                    statusColors[status] ?? 'bg-slate-50 text-slate-500 border-slate-100'
                  }`}
              >
                { status }
              </span>
            </div>

            <div className='mb-6'>
              <h4 className='text-lg font-black text-slate-800 mb-0.5 tracking-tight group-hover:text-emerald-600 transition-colors'>
                { org.organization.organizationName }
              </h4>
              <div className='flex items-center gap-1.5 text-slate-400 text-xs font-medium'>
                <svg className='w-3.5 h-3.5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                  <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.826a4 4 0 015.656 0l4 4a4 4 0 01-5.656 5.656l-1.103-1.103'
                  />
                </svg>
                { org.organization.primaryDomain }
              </div>
            </div>

            <div className='pt-6 border-t border-slate-50 flex justify-between items-center'>
              <div>
                <p className='text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5'>
                  Company Size
                </p>
                <p className='text-sm font-black text-slate-700'>
                  { org.subscription.companySize }
                </p>
              </div>
              <div className='text-right'>
                <p className='text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5'>
                  Interval
                </p>
                <p className='text-sm font-black text-slate-700 capitalize'>
                  { org.subscription.interval }
                </p>
              </div>
            </div>
          </div>
        );
      }) }

      <div className='bg-slate-50 p-6 rounded-[2rem] border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-center group cursor-pointer hover:border-emerald-300 hover:bg-emerald-50/30 transition-all duration-300 min-h-[220px]'>
        <div className='w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center mb-3 group-hover:scale-110 transition-transform'>
          <svg className='w-6 h-6 text-slate-400 group-hover:text-emerald-500' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 6v6m0 0v6m0-6h6m-6 0H6' />
          </svg>
        </div>
        <p className='text-slate-500 text-xs font-bold leading-tight'>
          Quick add new
          <br />
          organization
        </p>
      </div>
    </div>
  );
};

export default OrganizationsGrid;
